import { useMemo } from "react";
import { CalendarDays, Clock } from "lucide-react";
import { Badge, PRIORITY_COLORS, displayName, statusColor, statusLabel } from "./shared";

const todayISO = () => new Date().toISOString().slice(0, 10);

const dateLabel = (iso) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString("pt-BR", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });

export const AgendaView = ({ leads, onOpen }) => {
  const entries = useMemo(() => {
    const list = [];
    for (const lead of leads) {
      if (lead.type !== "service") continue;
      const appt = lead.payload?.appointment;
      if (appt?.date && appt?.time) list.push({ lead, date: appt.date, time: appt.time });
    }
    return list.sort((a, b) => `${a.date}T${a.time}`.localeCompare(`${b.date}T${b.time}`));
  }, [leads]);

  const days = useMemo(() => {
    const map = new Map();
    for (const e of entries) {
      if (!map.has(e.date)) map.set(e.date, []);
      map.get(e.date).push(e);
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [entries]);

  const today = todayISO();

  return (
    <div data-testid="agenda-view">
      <h1 className="font-display text-2xl font-bold text-petrol sm:text-3xl">Agenda</h1>
      <p className="mt-1 text-sm text-slate-500">
        Execuções agendadas de fato — confirmadas após orçamento aprovado e pago.
      </p>

      {days.length === 0 ? (
        <div className="mt-8 rounded-2xl border border-dashed border-slate-300 bg-white/60 py-20 text-center">
          <CalendarDays className="mx-auto h-8 w-8 text-slate-300" />
          <p className="mt-3 text-sm text-slate-500">
            Nenhum agendamento confirmado ainda. Abra uma demanda, confirme a data da execução e
            ela aparece aqui.
          </p>
        </div>
      ) : (
        <div className="mt-8 space-y-8">
          {days.map(([date, items]) => {
            const isPast = date < today;
            const isToday = date === today;
            return (
              <section key={date} data-testid={`agenda-day-${date}`} className={isPast ? "opacity-55" : ""}>
                <div className="flex items-center gap-3">
                  <h2 className="font-display text-base font-bold capitalize text-petrol">
                    {dateLabel(date)}
                  </h2>
                  {isToday && <Badge className="bg-brand/10 text-brand">hoje</Badge>}
                  {isPast && <Badge className="bg-slate-100 text-slate-500">passou</Badge>}
                  <span className="h-px flex-1 bg-slate-200" />
                </div>
                <ul className="mt-3 space-y-2">
                  {items.map((e) => (
                    <li key={e.lead.id}>
                      <button
                        onClick={() => onOpen(e.lead)}
                        data-testid={`agenda-entry-${e.lead.id}`}
                        className="group flex w-full items-center gap-4 rounded-2xl border border-slate-200 bg-white px-5 py-3.5 text-left transition-[border-color,box-shadow,transform] duration-200 hover:-translate-y-0.5 hover:border-brand/40 hover:shadow-md"
                      >
                        <span
                          className={`flex h-11 w-14 shrink-0 flex-col items-center justify-center rounded-xl font-mono text-sm font-bold ${
                            isToday ? "bg-brand text-white" : "bg-petrol text-white"
                          }`}
                        >
                          <Clock className="mb-0.5 h-3 w-3 opacity-70" />
                          {e.time}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-bold text-petrol">
                            {displayName(e.lead)}
                          </span>
                          <span className="mt-0.5 block truncate text-xs text-slate-500">
                            {e.lead.payload?.city}
                            {e.lead.payload?.service_type
                              ? `  ·  ${e.lead.payload.service_type}`
                              : "  ·  a classificar"}
                          </span>
                        </span>
                        <span className="flex shrink-0 flex-wrap items-center gap-2">
                          {e.lead.payload?.priority && (
                            <Badge
                              className={PRIORITY_COLORS[e.lead.payload.priority] || "bg-slate-100 text-slate-500"}
                            >
                              {e.lead.payload.priority}
                            </Badge>
                          )}
                          <Badge className={statusColor(e.lead.status)}>
                            {statusLabel(e.lead.status)}
                          </Badge>
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
};
